/**
 * @file alert-dialog.tsx
 * @description AlertDialog-примитив поверх @base-ui/react/alert-dialog
 * (phase19.13 этап 0).
 *
 * Отличие от `dialog.tsx`: диалог не закрывается по клику мимо и по Esc —
 * решение пользователь принимает кнопками. Используется там, где отмена
 * по неосторожности стоит дорого (проведение списания поверх резервов).
 *
 * API повторяет shadcn AlertDialog: Root/Trigger/Content/Header/Title/
 * Description/Footer/Cancel/Action.
 */

import { AlertDialog as AlertDialogPrimitive } from "@base-ui/react/alert-dialog";
import * as React from "react";

import { cn } from "@/lib/utils";

const AlertDialog = AlertDialogPrimitive.Root;
const AlertDialogTrigger = AlertDialogPrimitive.Trigger;
const AlertDialogPortal = AlertDialogPrimitive.Portal;
const AlertDialogCancel = AlertDialogPrimitive.Close;

function AlertDialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Backdrop>) {
  return (
    <AlertDialogPrimitive.Backdrop
      data-slot="alert-dialog-overlay"
      className={cn(
        "fixed inset-0 z-[var(--z-overlay)] bg-on-surface/30 backdrop-blur-sm",
        "data-[ending-style]:opacity-0 data-[starting-style]:opacity-0",
        "transition-opacity duration-200",
        className,
      )}
      {...props}
    />
  );
}

interface AlertDialogContentProps
  extends React.ComponentProps<typeof AlertDialogPrimitive.Popup> {
  /** На < sm разворачивается на весь экран (mobile-first). */
  fullScreen?: boolean;
}

function AlertDialogContent({
  className,
  children,
  fullScreen = false,
  ...props
}: AlertDialogContentProps) {
  return (
    <AlertDialogPortal>
      <AlertDialogOverlay />
      <AlertDialogPrimitive.Popup
        data-slot="alert-dialog-content"
        className={cn(
          // Слой строго выше overlay: backdrop-blur иначе размывает контент.
          // Шкала слоёв — `--z-*` в `index.css`.
          "fixed left-1/2 top-1/2 z-[var(--z-dialog)] -translate-x-1/2 -translate-y-1/2",
          "w-[calc(100%-2rem)] max-w-lg",
          "bg-surface-lowest rounded-xl",
          "[box-shadow:0_20px_40px_var(--shadow-ambient),inset_0_0_0_1px_var(--outline-variant)]",
          "p-6 flex flex-col gap-4",
          "data-[ending-style]:opacity-0 data-[starting-style]:opacity-0",
          "data-[ending-style]:scale-95 data-[starting-style]:scale-95",
          "transition-all duration-200",
          fullScreen &&
            "max-sm:inset-0 max-sm:h-full max-sm:w-full max-sm:max-w-none max-sm:translate-x-0 max-sm:translate-y-0 max-sm:overflow-y-auto max-sm:rounded-none",
          className,
        )}
        {...props}
      >
        {children}
      </AlertDialogPrimitive.Popup>
    </AlertDialogPortal>
  );
}

function AlertDialogHeader({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      data-slot="alert-dialog-header"
      className={cn("flex flex-col gap-1", className)}
      {...props}
    />
  );
}

function AlertDialogFooter({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      data-slot="alert-dialog-footer"
      className={cn(
        "flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end",
        className,
      )}
      {...props}
    />
  );
}

function AlertDialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Title>) {
  return (
    <AlertDialogPrimitive.Title
      data-slot="alert-dialog-title"
      className={cn("text-lg font-semibold tracking-tight text-on-surface", className)}
      {...props}
    />
  );
}

function AlertDialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Description>) {
  return (
    <AlertDialogPrimitive.Description
      data-slot="alert-dialog-description"
      className={cn("text-sm text-on-surface-variant", className)}
      {...props}
    />
  );
}

export {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogPortal,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
};
